import React, { useEffect, useState } from "react";
import { API_URL } from '../config';

export default function UserManagePage() {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  
  // ★追加：新規登録用の部署ステート
  const [department, setDepartment] = useState("");

  // メインスイッチ
  const [showManager, setShowManager] = useState(false);

  // 削除モードフラグ
  const [showDelete, setShowDelete] = useState(false);

  // 各ユーザーの新しいパスワードを保持するステート
  const [newPasswords, setNewPasswords] = useState({});

  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);

  const API_BASE = API_URL;

  // -------------------------
  // ユーザー一覧取得
  // -------------------------
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}?action=getUsers`, {
        credentials: "include",
      });
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("JSONではないレスポンスが返されました");
      }
      const data = await res.json();
      if (data.users) {
        setUsers(data.users);
        setMessage("");
        setError(false);
      } else {
        setMessage(data.error || "ユーザー一覧取得に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
      console.error(err);
    }
  };

  useEffect(() => {
    if (showManager) {
      fetchUsers();
    }
  }, [showManager]);

  // -------------------------
  // 新規登録
  // -------------------------
  const createUser = async () => {
    if (!username || !name || !password) {
      setMessage("すべて入力してください");
      setError(true);
      return;
    }
    try {
      const formData = new FormData();
      formData.append("username", username);
      formData.append("name", name);
      formData.append("password", password);
      formData.append("isAdmin", isAdmin ? 1 : 0);
      
      // ★追加：新規登録時にも部署データを送るようにする（※PHP側のcreate_userが未対応でもエラーにはなりません）
      formData.append("department", department);

      const res = await fetch(`${API_URL}?action=create_user`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setMessage("ユーザーを登録しました");
        setError(false);
        setUsername("");
        setName("");
        setPassword("");
        setIsAdmin(false);
        setDepartment(""); // クリア
        fetchUsers();
      } else {
        setMessage(data.error || "登録に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
    }
  };

  // -------------------------
  // 削除
  // -------------------------
  const deleteUser = async (id) => {
    if (!window.confirm("本当に削除しますか？\n削除すると元に戻せません。")) return;
    try {
      const formData = new FormData();
      formData.append("id", id);
      const res = await fetch(`${API_URL}?action=deleteUser`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setMessage("削除しました");
        setError(false);
        fetchUsers();
      } else {
        setMessage(data.error || "削除に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
    }
  };

  // -------------------------
  // 権限切り替え (Admin)
  // -------------------------
  const toggleAdmin = async (id, currentIsAdmin) => {
    const newStatus = Number(currentIsAdmin) === 1 ? 0 : 1;
    const actionName = newStatus === 1 ? "【管理者】" : "【一般ユーザー】";
    if (!window.confirm(`このユーザーを ${actionName} に変更しますか？`)) return;
    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("isAdmin", newStatus);
      const res = await fetch(`${API_URL}?action=toggle_admin`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`権限を変更しました`);
        setError(false);
        fetchUsers();
      } else {
        setMessage(data.error || "変更に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
    }
  };

  // -------------------------
  // 管理職フラグ切り替え (Manager)
  // -------------------------
  const toggleManager = async (id, currentIsManager) => {
    const newStatus = Number(currentIsManager) === 1 ? 0 : 1;
    const actionName = newStatus === 1 ? "【管理職】" : "【一般職】";
    
    if (!window.confirm(`このユーザーを ${actionName} に変更しますか？\n（管理職は深夜割増のみの計算になります）`)) return;

    try {
      const formData = new FormData();
      formData.append("action", "toggle_manager");
      formData.append("id", id);
      formData.append("isManager", newStatus);

      const res = await fetch(API_URL, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();

      if (data.success) {
        setMessage(`管理職設定を変更しました`);
        setError(false);
        fetchUsers(); 
      } else {
        setMessage(data.error || "変更に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
    }
  };

  // -------------------------
  // ★追加：部署設定切り替え (Department)
  // -------------------------
  const handleDepartmentChange = async (id, deptValue) => {
    try {
      const formData = new FormData();
      formData.append("action", "update_department");
      formData.append("id", id);
      formData.append("department", deptValue);

      const res = await fetch(API_URL, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();

      if (data.success) {
        setMessage(`部署設定を更新しました`);
        setError(false);
        fetchUsers(); 
      } else {
        setMessage(data.error || "部署の更新に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
    }
  };

  // -------------------------
  // パスワード強制上書き
  // -------------------------
  const handleUpdatePassword = async (id, userName) => {
    const newPass = newPasswords[id];
    if (!newPass) {
      alert("新しいパスワードを入力してください");
      return;
    }

    if (!window.confirm(`本当に「${userName}」のパスワードを上書きしますか？`)) return;

    try {
      const formData = new FormData();
      formData.append("action", "admin_change_password");
      formData.append("user_id", id);
      formData.append("new_password", newPass);

      const res = await fetch(API_URL, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      const data = await res.json();

      if (data.success) {
        setMessage(`「${userName}」のパスワードを更新しました`);
        setError(false);
        setNewPasswords({ ...newPasswords, [id]: "" });
      } else {
        setMessage(data.error || "パスワードの更新に失敗しました");
        setError(true);
      }
    } catch (err) {
      setMessage("通信エラーが発生しました");
      setError(true);
    }
  };

  return (
    <div style={{ padding: "10px 0" }}>
      
      {/* メインの開閉ボタン */}
      <button 
        onClick={() => setShowManager(!showManager)}
        style={{
          width: "100%",
          padding: "12px",
          backgroundColor: showManager ? "#6c757d" : "#007bff",
          color: "white",
          border: "none",
          borderRadius: "6px",
          fontSize: "16px",
          fontWeight: "bold",
          cursor: "pointer",
          marginBottom: "15px"
        }}
      >
        {showManager ? "▲ 閉じる" : "▼ ユーザー一覧を表示・編集する"}
      </button>

      {/* showManager が true の時だけ中身を表示 */}
      {showManager && (
        <div style={{ animation: "fadeIn 0.3s" }}>
          
          {/* メッセージ表示エリア */}
          {message && (
            <p style={{ color: error ? "red" : "green", fontWeight: "bold", marginBottom: "15px" }}>{message}</p>
          )}

          {/* 新規ユーザー登録フォーム */}
          <div style={{ marginBottom: "30px", border: "1px solid #ddd", padding: "20px", backgroundColor: "#f9f9f9", borderRadius: "8px" }}>
            <h3 style={{ marginTop: 0, marginBottom: "15px" }}>新規ユーザー登録</h3>

            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>ログインID (username)</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} style={{ width: "100%", maxWidth: "300px", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }} />
            </div>

            <div style={{ marginBottom: "10px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>表示名 (name)</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ width: "100%", maxWidth: "300px", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }} />
            </div>

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>パスワード</label>
              <input type="text" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: "100%", maxWidth: "300px", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }} />
            </div>

            {/* ★追加：新規登録フォームの所属部署選択欄 */}
            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontWeight: "bold", display: "block", marginBottom: "5px" }}>所属部署</label>
              <select value={department} onChange={(e) => setDepartment(e.target.value)} style={{ width: "100%", maxWidth: "300px", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}>
                <option value="">（未設定 / デフォルト）</option>
                <option value="SDC">SDC</option>
                <option value="制作技術">制作技術</option>
              </select>
            </div>

            <div style={{ marginBottom: "20px", display: "flex", justifyContent: "flex-start" }}>
              <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontWeight: "bold" }}>
                <input
                  type="checkbox"
                  checked={isAdmin}
                  onChange={(e) => setIsAdmin(e.target.checked)}
                  style={{ marginRight: "8px", transform: "scale(1.2)" }}
                />
                管理者として登録
              </label>
            </div>

            <button onClick={createUser} style={{ padding: "10px 20px", backgroundColor: "#28a745", color: "white", border: "none", borderRadius: "4px", cursor: "pointer", fontWeight: "bold" }}>
              登録する
            </button>
          </div>

          <hr style={{ margin: "30px 0", border: "none", borderTop: "1px solid #eee" }} />

          {/* ユーザー一覧エリア */}
          <h3 style={{ marginBottom: "15px" }}>ユーザー一覧</h3>

          <div style={{ 
            marginBottom: "15px", 
            padding: "15px", 
            backgroundColor: "#fff0f0", 
            border: "1px solid #ffcccc", 
            borderRadius: "5px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}>
            <label style={{ display: "flex", alignItems: "center", gap: "10px", cursor: "pointer", color: "#d32f2f", fontWeight: "bold" }}>
              <input 
                type="checkbox" 
                checked={showDelete} 
                onChange={(e) => setShowDelete(e.target.checked)} 
                style={{ transform: "scale(1.5)" }} 
              />
              <span>誤操作防止：チェックを入れると「削除ボタン」が表示されます</span>
            </label>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table border="1" cellPadding="8" style={{ width: "100%", borderCollapse: "collapse", minWidth: "800px" }}>
              <thead style={{ backgroundColor: "#f2f2f2" }}>
                <tr>
                  <th>ID</th>
                  <th>ログインID</th>
                  <th>名前</th>
                  <th>現在の権限</th>
                  <th>権限変更</th>
                  <th>管理職設定</th>
                  <th>所属部署設定</th> {/* ★追加：部署のヘッダー */}
                  <th>パスワード強制上書き</th>
                  {showDelete && <th style={{ backgroundColor: "#ffcccc", color: "red" }}>削除</th>}
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isUserAdmin = Number(u.isAdmin) === 1;
                  const isUserManager = Number(u.is_manager) === 1;

                  return (
                    <tr key={u.id} style={{ textAlign: "center" }}>
                      <td>{u.id}</td>
                      <td style={{ textAlign: "left" }}>{u.username}</td>
                      <td style={{ textAlign: "left" }}>{u.name}</td>
                      
                      <td>
                        {isUserAdmin ? (
                          <span style={{ color: "red", fontWeight: "bold" }}>管理者</span>
                        ) : (
                          "一般"
                        )}
                      </td>

                      <td>
                        <button
                          onClick={() => toggleAdmin(u.id, u.isAdmin)}
                          style={{
                            padding: "5px 10px",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            backgroundColor: isUserAdmin ? "#6c757d" : "#17a2b8",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {isUserAdmin ? "一般へ降格" : "管理者へ昇格"}
                        </button>
                      </td>

                      <td>
                        <button
                          onClick={() => toggleManager(u.id, u.is_manager)}
                          style={{
                            padding: "5px 10px",
                            color: isUserManager ? "white" : "black",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontWeight: "bold",
                            backgroundColor: isUserManager ? "#ff9800" : "#e0e0e0",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {isUserManager ? "★ 管理職" : "一般社員"}
                        </button>
                      </td>

                      {/* ★追加：所属部署の選択プルダウン列 */}
                      <td>
                        <select
                          value={u.department || ""}
                          onChange={(e) => handleDepartmentChange(u.id, e.target.value)}
                          style={{
                            padding: "5px",
                            borderRadius: "4px",
                            border: "1px solid #ccc",
                            backgroundColor: "#fff",
                            cursor: "pointer",
                            fontWeight: u.department ? "bold" : "normal"
                          }}
                        >
                          <option value="">（未設定）</option>
                          <option value="SDC">SDC</option>
                          <option value="制作技術">制作技術</option>
                        </select>
                      </td>

                      {/* パスワード上書き列 */}
                      <td style={{ backgroundColor: "#fff9e6" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                          <input
                            type="text"
                            placeholder="新パスワード"
                            value={newPasswords[u.id] || ""}
                            onChange={(e) => setNewPasswords({ ...newPasswords, [u.id]: e.target.value })}
                            style={{ width: "120px", padding: "4px", border: "1px solid #ccc", borderRadius: "3px" }}
                          />
                          <button
                            onClick={() => handleUpdatePassword(u.id, u.name)}
                            style={{
                              padding: "5px 10px",
                              backgroundColor: "#ffc107",
                              color: "black",
                              fontWeight: "bold",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              whiteSpace: "nowrap"
                            }}
                          >
                            変更
                          </button>
                        </div>
                      </td>

                      {showDelete && (
                        <td style={{ backgroundColor: "#ffcccc" }}>
                          <button 
                            onClick={() => deleteUser(u.id)}
                            style={{ padding: "5px 10px", backgroundColor: "#dc3545", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                          >
                            削除
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}